import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ASSETS } from '../config/assets';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Link } from 'react-router-dom';
import { MapPin, Calendar, Ruler, CheckCircle2, ArrowRight, Building, Home, GraduationCap, Heart, Zap } from 'lucide-react';

const PortfolioPage = () => {
  const [selectedPortfolio, setSelectedPortfolio] = useState(0);

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.3 },
    transition: { duration: 0.35 }
  };

  const portfolios = [
    {
      id: 'doltone-house',
      title: 'Doltone House Reception Venue',
      subtitle: 'Luxury Wedding Venue',
      icon: Heart,
      location: 'Sylvania Waters, Sydney',
      area: '1,000+ SQM',
      year: '2024',
      finish: '800 Grit High Gloss',
      description: 'Premium polished concrete flooring for one of Sydney\'s most prestigious wedding venues. A showcase of our high-gloss finishing capabilities and attention to detail.',
      images: [
        ...ASSETS.images.doltoneHouse,
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/k5uy2mcn_WhatsApp-Image-2025-02-01-at-14.11.49-1.jpeg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/ndtvx23p_WhatsApp-Image-2025-02-01-at-14.11.52-1.jpeg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/tkhh1ke5_WhatsApp-Image-2025-02-01-at-14.11.492-1.jpeg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/w6b3hil7_WhatsApp-Image-2025-02-01-at-14.12.18-1.jpeg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/6ipgtkl5_Doltone-House-Sylvania-Waters-Le-Jardin-98-.jpeg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/fzy6x96f_DSC00170-scaled.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/c3prgagy_image-4.jpeg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/jhn7365v_La-Maison-5-scaled.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/kpr6ny8v_dipino_concrete_construction__20230317_p_3060279116184876458_2_3060279109784406791.webp',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/ff3obe79_dipino_concrete_construction__20230317_p_3060279116184876458_4_3060279109775861723.webp',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/m2va6z1a_dipino_concrete_construction__20230317_p_3060279116184876458_3_3060279109968982962.webp',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/ibxlgmi8_dipino_concrete_construction__20230317_p_3060279116184876458_1_3060279109767403915.webp'
      ],
      video: 'c0kiqcfeB4U', // Doltone House project video
      shorts: [
        {
          id: 'msH4CMdhNRc',
          title: 'Doltone House Wedding Short',
          description: 'Quick showcase of wedding venue'
        },
        {
          id: 'WhfwR5Uq2QI',
          title: 'Doltone House Showcase',
          description: 'Additional venue highlights and features'
        },
        {
          id: 'wrFgqzHORzI',
          title: 'Benchtop Grind & Polish',
          description: 'Professional benchtop restoration and polishing services'
        },
        {
          id: 'hy7FbYQTikQ',
          title: 'Polished Concrete Detail',
          description: 'Close-up showcase of premium polished concrete finish'
        },
        {
          id: '-tvL_dJnxYs',
          title: 'Wedding Venue Excellence',
          description: 'Premium wedding venue polished concrete showcase'
        }
      ],
      highlights: [
        'Mirror-like finish reflecting ambient lighting',
        'Heavy foot traffic durability for wedding events', 
        'Elegant aesthetic enhancing venue sophistication',
        'Low maintenance for operational efficiency'
      ],
      type: 'Commercial - Hospitality'
    },
    {
      id: 'camden-civic',
      title: 'Camden Civic Center',
      subtitle: 'Municipal Building',
      icon: Building,
      location: 'Camden, Sydney',
      area: '800+ SQM',
      year: '2023',
      finish: 'Full Aggregate Exposure',
      description: 'Large-scale commercial polished concrete installation for the Camden Civic Center. High-traffic municipal building requiring durable, professional-grade flooring.',
      images: [
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/ry4jdnnu_20250810_141512.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/2221hn0i_20250810_141505.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/u402hpt7_20250810_141316.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/acenxiek_20250810_141308.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/ktgxj01z_20250810_140323.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/e67wi1iu_20250810_140633.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/ls9ciyk7_20250810_141956.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/m452xgom_20250810_141534.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/ljcj511u_20250809_101655.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/a4vye802_20250809_102241.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/r8ia6wea_20250810_121450.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/41hegsao_20250810_121501.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/z82wjbqt_20250810_121513.jpg'
      ],
      video: 'Fst8nciAdxU', // Camden Civic Center before and after video
      shorts: [
        {
          id: 'LmBgGRaP960',
          title: 'Camden Civic Center Short',
          description: 'Quick showcase of municipal polished concrete'
        }
      ],
      highlights: [
        'High-traffic public building durability',
        'Professional appearance for civic space',
        'Cost-effective long-term maintenance',
        'Slip-resistant safety compliance'
      ],
      type: 'Commercial - Municipal'
    },
    {
      id: 'coogee-beach-home',
      title: 'Award Winning Home: Major St, Coogee Beach',
      subtitle: 'Luxury Residential',
      icon: Home,
      location: 'Coogee Beach, Sydney',
      area: '200+ SQM',
      year: '2022',
      finish: 'Nil Exposure Polish',
      description: 'Award-winning luxury residential home featuring nil exposure mechanically polished concrete. Ocean-view property showcasing premium residential concrete finishing.',
      images: [
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/uyl4hz5e_20240213_111936.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/5o5qbvyo_20240213_112657.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/619ajg5l_20240213_111954.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/4mkpqi43_20240213_111416.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/swl4s4uc_20240213_111424.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/tjn83kt1_20240213_111427.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/obyzpbh1_20240213_111438.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/c7adcj0f_20240211_160715.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/i6yd5707_20240211_160722.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/ly93jhlv_20240211_160728.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/mr786t8m_20240211_160712.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/53wkgs0r_20240211_160907.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/3q7t3m5j_20240211_160900.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/829ob84z_20240211_160924.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/1zfl2h45_20240211_160927.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/w3h3vj45_20240211_160930.jpg'
      ], // Real Coogee Beach home images
      video: 'Xz-PZdWdRWE', // Award Winning Home project video
      videos: [
        {
          id: 'Xz-PZdWdRWE',
          title: 'Award Winning Home - Before Furniture',
          description: 'See the polished concrete transformation without furniture'
        },
        {
          id: 'EkepD-EIPFk', 
          title: 'Award Winning Home - With Furniture',
          description: 'Complete home showcase with furniture and ocean views'
        }
      ],
      highlights: [
        'Award-winning architectural integration',
        'Nil exposure finish for modern aesthetic',
        'Ocean-view property premium quality',
        'Residential luxury standard achievement'
      ],
      type: 'Residential - Luxury'
    },
    {
      id: 'college-rocks',
      title: 'AIM College - The Rocks Grind & Seal',
      subtitle: 'Educational Institution',
      icon: GraduationCap,
      location: 'The Rocks, Sydney',
      area: '1000+ SQM',
      year: '2023',
      finish: 'Grind & Seal',
      description: 'Educational facility polished concrete installation in historic The Rocks area. Large-scale project requiring durability and aesthetic appeal for student and faculty areas.',
      images: [
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/hwjq56do_20211215_001559.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/071pybew_20211214_220557.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/xqg0n5sn_20211215_001619.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/47j3s8da_20211215_001559.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/mm5nhfmy_20211214_220454.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/3hqwz9r1_20211214_220423.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/nle41e7s_20211214_220419.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/zsourjzq_20211214_220408.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/te6njf7d_20211214_220346.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/9rxguhup_20211213_230230.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/u29ewaw0_20211213_230212.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/619hdn5n_20211213_230152.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/rtaogi4f_20211213_230058.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/9wtygibp_20211213_230046.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/hvydsd8m_20211213_230036.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/ughsjc0a_20211213_230004.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/qlitmj25_20211213_225958.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/jy493prb_20211213_225946.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/jk79wbv6_20211213_225813.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/vlaly2ie_20211213_225802.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/cv3e2t7i_20211213_225735.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/mr104v2b_20211213_225716.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/50odqujt_20211213_225649.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/gzujf962_20211213_225642.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/ghcam6ef_20211213_225634.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/ajoyyhg2_20211213_225548.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/2qggyybs_20211213_225521.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/8hfczvkw_20211213_225422.jpg'
      ], // Real AIM College images
      highlights: [
        'Historic area architectural sensitivity',
        'Large-scale educational facility',
        'Student-safe slip resistance',
        'Long-term durability for high use'
      ],
      type: 'Commercial - Education'
    },
    {
      id: 'bodyfit-gym',
      title: 'Bodyfit Gym Fitness & Strength',
      subtitle: 'Commercial Fitness Facility',
      icon: Zap,
      location: 'Shellharbour, NSW',
      area: 'Approx 200 SQM',
      year: '2022',
      finish: 'Full Aggregate Exposure',
      description: 'High-performance fitness facility featuring full aggregate exposure polished concrete. Heavy-duty flooring designed to withstand gym equipment and high-intensity workouts while maintaining a professional appearance.',
      images: [
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/pe93zrcc_maxresdefault.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/3mumkmej_maxresdefault.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/p5w1jomx_Body-Fit-10.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/ej7e8t3o_Body-Fit-11.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/mfqm5567_Body-Fit-12.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/rcbuobwx_Body-Fit-13.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/5ijs6xzw_Body-Fit-21.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/rhcp1hoy_Body-Fit-22.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/4i7m1zfr_Body-Fit-24.jpg',
        'https://customer-assets.emergentagent.com/job_concretepro/artifacts/hiny65u7_Body-Fit-29.jpg'
      ],
      video: 'jG7W8HbQZ_Q', // Bodyfit Gym project video
      highlights: [
        'Heavy equipment load resistance',
        'Slip-resistant safety for gym activities',
        'Easy maintenance for high-traffic fitness area',
        'Professional aesthetic for commercial gym'
      ],
      type: 'Commercial - Fitness'
    }
  ];

  return (
    <div className="min-h-screen pt-16" data-testid="portfolio-page">
      {/* Hero */}
      <section className="section-spacing bg-gradient-to-br from-slate-100 via-slate-50 to-slate-100 dark:from-slate-800 dark:via-slate-900 dark:to-slate-800">
        <div className="container-custom text-center">
          <motion.div {...fadeInUp}>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-slate-800 dark:text-slate-100">
              Featured Portfolio
            </h1>
            <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
              Showcasing our premium polished concrete work across commercial, residential, and institutional projects
            </p>
          </motion.div>
        </div>
      </section>

      {/* Portfolio Navigation */}
      <section className="section-spacing bg-background">
        <div className="container-custom">
          <motion.div {...fadeInUp} className="mb-16">
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
              {portfolios.map((portfolio, index) => (
                <Card 
                  key={portfolio.id}
                  className={`cursor-pointer transition-all duration-300 hover:shadow-lg ${
                    selectedPortfolio === index 
                      ? 'ring-2 ring-primary ring-offset-2 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950 dark:to-orange-950' 
                      : 'hover:bg-accent'
                  }`}
                  onClick={() => setSelectedPortfolio(index)}
                  data-testid={`portfolio-nav-${index}`}
                >
                  <CardContent className="p-6 text-center">
                    <portfolio.icon className="w-12 h-12 mx-auto mb-4 text-primary" />
                    <h3 className="font-bold text-lg mb-3 text-slate-800 dark:text-slate-100 leading-tight">
                      {portfolio.title}
                    </h3>
                    <Badge variant="secondary" className="mb-3">
                      {portfolio.type}
                    </Badge>
                    <p className="text-sm text-slate-600 dark:text-slate-300 mb-2">
                      {portfolio.location}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                      📷 View Project Gallery
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>

          {/* Selected Portfolio Details */}
          <motion.div
            key={selectedPortfolio}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-16"
          >
            {/* Hero Image */}
            <div className="relative aspect-[21/9] w-full overflow-hidden rounded-2xl mb-12">
              <img
                src={portfolios[selectedPortfolio].images[0]}
                alt={`${portfolios[selectedPortfolio].title} Main`}
                className="w-full h-full object-cover"
                data-testid="selected-portfolio-hero"
              />
              <div className="absolute top-6 left-6">
                <Badge className="bg-amber-600 hover:bg-amber-700 text-white">
                  {portfolios[selectedPortfolio].type}
                </Badge>
              </div>
            </div>

            {/* Project Title & Description */}
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-slate-800 dark:text-slate-100">
                {portfolios[selectedPortfolio].title}
              </h2>
              <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
                {portfolios[selectedPortfolio].description}
              </p>
            </div>

            {/* Project Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              <Card>
                <CardContent className="pt-6 text-center">
                  <Ruler className="h-8 w-8 mx-auto mb-3 text-primary" />
                  <p className="text-2xl font-bold mb-1 text-slate-800 dark:text-slate-100">
                    {portfolios[selectedPortfolio].area}
                  </p>
                  <p className="text-sm text-slate-600 dark:text-slate-300">Area</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <MapPin className="h-8 w-8 mx-auto mb-3 text-primary" />
                  <p className="text-2xl font-bold mb-1 text-slate-800 dark:text-slate-100">
                    {portfolios[selectedPortfolio].location}
                  </p>
                  <p className="text-sm text-slate-600 dark:text-slate-300">Location</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <Calendar className="h-8 w-8 mx-auto mb-3 text-primary" />
                  <p className="text-2xl font-bold mb-1 text-slate-800 dark:text-slate-100">
                    {portfolios[selectedPortfolio].year}
                  </p>
                  <p className="text-sm text-slate-600 dark:text-slate-300">Completed</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <CheckCircle2 className="h-8 w-8 mx-auto mb-3 text-primary" />
                  <p className="text-2xl font-bold mb-1 text-slate-800 dark:text-slate-100">
                    {portfolios[selectedPortfolio].finish}
                  </p>
                  <p className="text-sm text-slate-600 dark:text-slate-300">Finish</p>
                </CardContent>
              </Card>
            </div>

            {/* Project Gallery */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6 text-center text-slate-800 dark:text-slate-100">
                Project Gallery
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {portfolios[selectedPortfolio].images.slice(1).map((image, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-300">
                      <img
                        src={image}
                        alt={`${portfolios[selectedPortfolio].title} ${index + 2}`}
                        className="w-full aspect-[4/3] object-cover"
                        data-testid={`portfolio-gallery-${index}`}
                      />
                    </Card>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Project Video(s) - Show for portfolios with videos */}
            {(portfolios[selectedPortfolio].video || portfolios[selectedPortfolio].videos) && (
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-6 text-center text-slate-800 dark:text-slate-100">
                  {portfolios[selectedPortfolio].videos ? 'Project Videos' : 'Project Video'}
                </h3>
                
                {/* Multiple Videos Layout */}
                {portfolios[selectedPortfolio].videos ? (
                  <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
                    {portfolios[selectedPortfolio].videos.map((video, index) => (
                      <Card key={video.id} className="overflow-hidden">
                        <div className="relative aspect-video">
                          <iframe
                            src={`https://www.youtube.com/embed/${video.id}?hd=1&vq=hd1080&rel=0&modestbranding=1`}
                            title={video.title}
                            className="w-full h-full"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                            data-testid={`portfolio-project-video-${index}`}
                          />
                        </div>
                        <CardContent className="p-4 text-center bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-700">
                          <h4 className="text-lg font-semibold mb-2 text-slate-800 dark:text-slate-100">
                            {video.title}
                          </h4>
                          <p className="text-sm text-slate-600 dark:text-slate-300">
                            {video.description}
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  /* Single Video Layout */
                  <Card className="overflow-hidden max-w-4xl mx-auto">
                    <div className="relative aspect-video">
                      <iframe
                        src={`https://www.youtube.com/embed/${portfolios[selectedPortfolio].video}?hd=1&vq=hd1080&rel=0&modestbranding=1`}
                        title={`${portfolios[selectedPortfolio].title} Project Video`}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        data-testid="portfolio-project-video"
                      />
                    </div>
                    <CardContent className="p-6 text-center bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-700">
                      <h4 className="text-lg font-semibold mb-2 text-slate-800 dark:text-slate-100">
                        {portfolios[selectedPortfolio].title} - Project Showcase
                      </h4>
                      <p className="text-slate-600 dark:text-slate-300">
                        Watch the complete transformation and see our polished concrete expertise in action
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Portfolio Shorts - Show for portfolios with shorts */}
            {portfolios[selectedPortfolio].shorts && (
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-6 text-center text-slate-800 dark:text-slate-100">
                  Project Shorts
                </h3>
                <div className={`gap-6 mx-auto ${portfolios[selectedPortfolio].shorts.filter(short => short.id).length === 1 ? 'flex justify-center max-w-xs' : portfolios[selectedPortfolio].shorts.filter(short => short.id).length <= 3 ? 'grid md:grid-cols-3 max-w-4xl' : 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 max-w-6xl'}`}>
                  {portfolios[selectedPortfolio].shorts.filter(short => short.id).map((short, index) => (
                    <Card key={short.id} className="overflow-hidden">
                      <div className="relative aspect-[9/16]">
                        <iframe
                          src={`https://www.youtube.com/embed/${short.id}?hd=1&vq=hd1080&rel=0&modestbranding=1`}
                          title={short.title}
                          className="w-full h-full"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                        />
                        {/* Shorts Label */}
                        <div className="absolute top-3 left-3 bg-red-600 text-white px-2 py-1 text-xs rounded font-medium pointer-events-none z-10">
                          Shorts
                        </div>
                      </div>
                      <CardContent className="p-4 text-center">
                        <h4 className="font-medium mb-2 text-slate-800 dark:text-slate-100">
                          {short.title}
                        </h4>
                        <p className="text-sm text-slate-600 dark:text-slate-300">
                          {short.description}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Project Highlights */}
            <div className="grid md:grid-cols-2 gap-8">
              <Card>
                <CardHeader>
                  <CardTitle className="text-slate-800 dark:text-slate-100">
                    Project Highlights
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {portfolios[selectedPortfolio].highlights.map((highlight, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-slate-600 dark:text-slate-300">{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-slate-800 dark:text-slate-100">
                    Why This Project Stands Out
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 dark:text-slate-300 mb-4">
                    Each project represents our commitment to excellence and showcases different 
                    aspects of our polished concrete expertise.
                  </p>
                  <div className="flex space-x-4">
                    <Button 
                      variant="outline"
                      size="sm"
                      className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                      asChild
                    >
                      <Link to="/services" data-testid="view-services-button">
                        View Our Services
                      </Link>
                    </Button>
                    <Button 
                      variant="outline"
                      size="sm"
                      className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                      asChild
                    >
                      <Link to="/contact" data-testid="get-quote-button">
                        Get Quote
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing bg-gradient-to-r from-slate-900 to-slate-800 dark:from-slate-100 dark:to-slate-200 text-white dark:text-slate-900">
        <div className="container-custom text-center">
          <motion.div {...fadeInUp}>
            <h2 className="text-3xl font-bold mb-4">Ready for Your Own Transformation?</h2>
            <p className="mb-8 max-w-2xl mx-auto opacity-90">
              Let us bring the same level of quality and craftsmanship to your project. 
              From residential homes to commercial spaces, we deliver excellence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white border-0"
                asChild
              >
                <Link to="/contact" data-testid="portfolio-main-cta">
                  Start Your Project <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button 
                variant="outline"
                size="lg"
                className="border-white text-white hover:bg-white hover:text-slate-900 dark:border-slate-900 dark:text-slate-900 dark:hover:bg-slate-900 dark:hover:text-white"
                asChild
              >
                <Link to="/services" data-testid="portfolio-services-cta">
                  View All Services
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PortfolioPage;
